
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, ChevronDown, ChevronRight } from "lucide-react";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});

  // Navigation links used in both desktop and mobile views
  const navLinks = [
    { to: "/ai-music-framework", label: "AI Music Framework" }
  ];

  const productItems = [
    { label: "Copyrightability Engine", href: "/copyrightability-engine" },
    { label: "TAG Plugin for DAW", href: "/copyrightability-engine#tag-plugin" }
  ];

  const resourceItems = [
    { label: "Videos", href: "#videos" },
    { label: "Blogs", href: "#blogs" }
  ];

  const companyItems = [
    { label: "About us", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Join the Waitlist", href: "/join-waitlist" }
  ];

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <nav className="w-full px-6 py-5 flex justify-between items-center lg:px-[36px]">
      <div className="flex items-center">
        <Link to="/" className="flex items-center text-white">
          <img src="/lovable-uploads/9ef63189-30d4-440c-aa62-4eb2d6d0c142.png" alt="Tagmusic Logo" className="h-10" />
        </Link>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:flex space-x-8 text-white items-center">
        {/* Products Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1 hover:text-gray-200 transition-colors">
            Products
            <ChevronDown size={16} className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            className="bg-white/95 backdrop-blur-sm border border-tagblue/20 shadow-xl rounded-xl min-w-[200px] mt-2"
            align="start"
          >
            {productItems.map((item, index) => (
              <DropdownMenuItem 
                key={index}
                className="hover:bg-tagblue/10 focus:bg-tagblue/10 transition-colors rounded-lg mx-1 my-1"
                asChild
              >
                <a 
                  href={item.href}
                  className="block px-3 py-2 text-gray-700 hover:text-tagblue font-medium cursor-pointer"
                >
                  {item.label}
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* AI Music Framework Link */}
        <Link 
          to="/ai-music-framework" 
          className="hover:text-gray-200 transition-colors"
        >
          AI Music Framework
        </Link>

        {/* Resources Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1 hover:text-gray-200 transition-colors">
            Resources
            <ChevronDown size={16} className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            className="bg-white/95 backdrop-blur-sm border border-tagblue/20 shadow-xl rounded-xl min-w-[200px] mt-2"
            align="start"
          >
            {resourceItems.map((item, index) => (
              <DropdownMenuItem 
                key={index}
                className="hover:bg-tagblue/10 focus:bg-tagblue/10 transition-colors rounded-lg mx-1 my-1"
                asChild
              >
                <a 
                  href={item.href}
                  className="block px-3 py-2 text-gray-700 hover:text-tagblue font-medium cursor-pointer"
                >
                  {item.label}
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Company Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1 hover:text-gray-200 transition-colors">
            Company
            <ChevronDown size={16} className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            className="bg-white/95 backdrop-blur-sm border border-tagblue/20 shadow-xl rounded-xl min-w-[200px] mt-2"
            align="start"
          >
            {companyItems.map((item, index) => (
              <DropdownMenuItem 
                key={index}
                className="hover:bg-tagblue/10 focus:bg-tagblue/10 transition-colors rounded-lg mx-1 my-1"
                asChild
              >
                <a 
                  href={item.href}
                  className="block px-3 py-2 text-gray-700 hover:text-tagblue font-medium cursor-pointer"
                >
                  {item.label}
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Mobile Navigation */}
      <div className="md:hidden">
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerTrigger asChild>
            <button className="text-white p-2" aria-label="Menu">
              <Menu size={24} />
            </button>
          </DrawerTrigger>
          <DrawerContent className="bg-tagblue">
            <div className="py-4 flex flex-col items-center">
              {/* Products Section */}
              <div className="w-full mb-2">
                <button 
                  onClick={() => toggleSection('products')}
                  className="flex items-center justify-between text-white text-lg font-semibold px-4 py-3 w-full hover:bg-tagblue-dark/50 transition-colors"
                >
                  Products
                  <ChevronRight 
                    size={16} 
                    className={`transition-transform duration-200 ${expandedSections.products ? 'rotate-90' : ''}`} 
                  />
                </button>
                {expandedSections.products && (
                  <div className="bg-tagblue-dark/30">
                    {productItems.map((item, index) => (
                      <a
                        key={index}
                        href={item.href}
                        className="block text-white/80 py-2 px-8 text-base hover:bg-tagblue-dark/50 transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              
              {/* AI Music Framework Link */}
              <Link
                to="/ai-music-framework"
                className="text-white py-3 text-lg hover:bg-tagblue-dark/50 w-full text-left px-4 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                AI Music Framework
              </Link>
              
              {/* Resources Section */}
              <div className="w-full mb-2">
                <button 
                  onClick={() => toggleSection('resources')}
                  className="flex items-center justify-between text-white text-lg font-semibold px-4 py-3 w-full hover:bg-tagblue-dark/50 transition-colors"
                >
                  Resources
                  <ChevronRight 
                    size={16} 
                    className={`transition-transform duration-200 ${expandedSections.resources ? 'rotate-90' : ''}`} 
                  />
                </button>
                {expandedSections.resources && (
                  <div className="bg-tagblue-dark/30">
                    {resourceItems.map((item, index) => (
                      <a
                        key={index}
                        href={item.href}
                        className="block text-white/80 py-2 px-8 text-base hover:bg-tagblue-dark/50 transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Company Section */}
              <div className="w-full mb-2">
                <button 
                  onClick={() => toggleSection('company')}
                  className="flex items-center justify-between text-white text-lg font-semibold px-4 py-3 w-full hover:bg-tagblue-dark/50 transition-colors"
                >
                  Company
                  <ChevronRight 
                    size={16} 
                    className={`transition-transform duration-200 ${expandedSections.company ? 'rotate-90' : ''}`} 
                  />
                </button>
                {expandedSections.company && (
                  <div className="bg-tagblue-dark/30">
                    {companyItems.map((item, index) => (
                      <a
                        key={index}
                        href={item.href}
                        className="block text-white/80 py-2 px-8 text-base hover:bg-tagblue-dark/50 transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </nav>
  );
};

export default Navbar;
